import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, CalendarDays, KeyRound, ArrowUpRight } from "lucide-react";
import Masthead from "@/components/alcott/Masthead";
import Seo from "@/components/Seo";
import AlcottFooter from "@/components/alcott/AlcottFooter";
import HorizonWaitlist from "@/components/alcott/HorizonWaitlist";
import PageHero from "@/components/alcott/PageHero";
import { EXPERIENCES, SANCTUM_EVENTS } from "@/data/alcott";

const ease = [0.22, 1, 0.36, 1];

const TIER_COLOR = {
  "The Navigator Circle": "bg-[#0A0A0A]/8 text-[#0A0A0A]",
  "The Horizon Fellow": "bg-[#C05C42]/15 text-[#C05C42]",
  "The Apex Guild": "bg-[#0A0A0A] text-[#FAF9F6]",
};

export const Experiences = () => {
  const [waitlist, setWaitlist] = useState(null);

  return (
    <div className="min-h-screen bg-[#FAF9F6] font-body text-[#0A0A0A]" data-testid="experiences-page">
      <Seo title="See & Do" description="Private culinary salons, boutique heritage retreats, and international gatherings with The Horizon Circle — culture meets global mobility, across three tiers of membership." />
      <Masthead />

      <PageHero
        overline="The Horizon Circle"
        title="Experiences & Events"
        blurb="Private culinary salons, boutique heritage retreats, and international gatherings. Access corresponds to your membership tier — join the waitlist and the Circle will be in touch."
      />

      <section className="mx-auto max-w-[1500px] px-5 py-16 md:px-10 md:py-20">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {EXPERIENCES.map((e, i) => (
            <motion.article
              key={e.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.08, ease }}
              className="group overflow-hidden rounded-2xl border border-[#0A0A0A]/12 bg-[#FAF9F6]"
              data-testid={`experience-${e.id}`}
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={e.image} alt={e.title} className="h-full w-full object-cover transition-transform duration-[1000ms] group-hover:scale-105" />
                <span className={`absolute left-4 top-4 rounded-full px-3 py-1 font-body text-[0.55rem] uppercase tracking-[0.18em] ${TIER_COLOR[e.tier] || "bg-[#0A0A0A]/8 text-[#0A0A0A]"}`}>
                  {e.tier}
                </span>
              </div>
              <div className="p-7">
                <p className="font-body text-[0.62rem] uppercase tracking-[0.22em] text-[#C05C42]">{e.type}</p>
                <h3 className="mt-2 font-serif text-3xl font-semibold leading-tight tracking-tight">{e.title}</h3>
                <p className="mt-3 font-body text-sm leading-relaxed text-[#0A0A0A]/60">{e.excerpt}</p>
                <div className="mt-5 flex flex-wrap gap-5 font-body text-[0.7rem] uppercase tracking-[0.14em] text-[#0A0A0A]/50">
                  <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-[#C05C42]" /> {e.location}</span>
                  <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} className="text-[#C05C42]" /> {e.date}</span>
                </div>
                <button
                  onClick={() => setWaitlist(`${e.title} · ${e.tier}`)}
                  className="mt-7 w-full rounded-full border border-[#0A0A0A]/20 py-3.5 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#0A0A0A] transition-colors duration-300 hover:border-[#C05C42] hover:bg-[#C05C42] hover:text-[#FAF9F6]"
                  data-testid={`experience-waitlist-${e.id}`}
                >
                  Join the waitlist
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* From the Inner Sanctum — gated gatherings */}
      <section className="relative overflow-hidden bg-[#0A0A0A] px-5 py-20 text-[#FAF9F6] md:px-10 md:py-28" data-testid="sanctum-events">
        <div
          className="pointer-events-none absolute right-0 top-0 h-[34rem] w-[34rem] translate-x-1/3 -translate-y-1/4 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(197,160,89,0.18), transparent 62%)" }}
        />
        <div className="relative mx-auto max-w-[1500px]">
          <div className="max-w-2xl">
            <p className="font-body text-[0.7rem] uppercase tracking-[0.3em] text-[#C5A059]">From the Inner Sanctum</p>
            <h2 className="mt-4 font-serif text-[clamp(2rem,4.5vw,3.4rem)] font-light leading-[1.02] tracking-tight">
              Rites & Gatherings of Sybils Society
            </h2>
            <p className="mt-5 font-body text-base leading-relaxed text-[#FAF9F6]/60">
              Beyond the Horizon Circle lies the Society's gated calendar — convenings whose access is held by sigil.
              These are entered through the Veil.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2">
            {SANCTUM_EVENTS.map((e, i) => (
              <motion.article
                key={e.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-8%" }}
                transition={{ duration: 0.7, delay: (i % 2) * 0.08, ease }}
                className="group overflow-hidden rounded-2xl border border-[#C5A059]/20 bg-[#FAF9F6]/[0.03]"
                data-testid={`sanctum-event-${e.id}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={e.image} alt={e.title} className="h-full w-full object-cover opacity-80 transition-all duration-[1000ms] group-hover:scale-105 group-hover:opacity-100" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                  <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A]/70 px-3 py-1 font-body text-[0.55rem] uppercase tracking-[0.18em] text-[#C5A059] backdrop-blur-sm">
                    <KeyRound size={11} /> {e.access}
                  </span>
                </div>
                <div className="p-7">
                  <p className="font-body text-[0.62rem] uppercase tracking-[0.22em] text-[#C5A059]">{e.type}</p>
                  <h3 className="mt-2 font-serif text-2xl font-semibold leading-tight tracking-tight">{e.title}</h3>
                  <p className="mt-3 font-body text-sm leading-relaxed text-[#FAF9F6]/55">{e.excerpt}</p>
                  <div className="mt-5 flex items-center gap-2 font-body text-[0.7rem] uppercase tracking-[0.14em] text-[#FAF9F6]/45">
                    <CalendarDays size={13} className="text-[#C5A059]" /> {e.cadence}
                  </div>
                  <Link
                    to="/veil"
                    onClick={() => sessionStorage.removeItem("sybil_entered")}
                    className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#C5A059]/40 py-3.5 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#0A0A0A]"
                    data-testid={`sanctum-enter-${e.id}`}
                  >
                    Enter the Sanctum <ArrowUpRight size={13} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <AlcottFooter onJoin={() => setWaitlist("Experiences & Events")} />
      <HorizonWaitlist open={!!waitlist} onClose={() => setWaitlist(null)} context={waitlist} />
    </div>
  );
};

export default Experiences;
